<template>
  <div style="display: flex; flex-direction: column; align-items: flex-start; gap: 8px">
    <div
      v-if="reactions.length > 0"
      :style="{
        display: 'flex',
        width: '100%',
        flexWrap: 'nowrap',
        marginTop: '4px',
        opacity: !isActive ? '0.5' : '1',
        pointerEvents: !isActive ? 'none' : 'auto'
      }"
    >
      <iReactionViewer
        :reactions="reactionsWithUserData"
        teleport-to-body
        @toggle:reaction-picker="$emit('toggle:reaction-picker', $event)"
        @reaction-selected="(event) => reactionSelected(event)"
      />
    </div>
    <div
      v-if="canReply && numReplies > 0"
      class="reply-btn"
      :style="{
        display: 'flex',
        gap: '4px',
        flexWrap: 'nowrap',
        marginTop: '4px',
        cursor: 'pointer',
        opacity: !isActive ? '0.5' : '1',
        pointerEvents: !isActive ? 'none' : 'auto'
      }"
      @click="reply"
    >
      <div style="display: flex; align-items: center; gap: 5px">
        <img
          :alt="latestReply.authorName"
          :src="latestReply.avatarUrl || DEFAULT_USER_AVATAR"
          style="width: 20px; height: 20px; object-fit: cover; border-radius: 5px"
        >
        <a
          style="font-size: 14px; color: #0f589f; text-decoration: none; cursor: pointer"
          @click.prevent="reply"
        >
          {{ getReplies }}
        </a>
        <span
          class="reply-time"
          style="font-size: 14px; color: #6b7280"
        >
          {{ latestTimestampCreated }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useMessageStore } from "%/apps/conversations/stores/message-store";
import { useGeneralStore } from "%/stores/general-store";
import { timeAgo } from "~/helpers/publication-helper";
import { DEFAULT_USER_AVATAR } from "%/constants/user-constants";
import { OFFENSE_OPTIONS } from "%/constants/conversations-constants";

export default {
  name: "MessageFooter",
  props: {
    messageId: {
      type: Number,
      required: true,
    },
  },
  emits: ["toggle:reaction-picker", "open:reply-modal"],
  data() {
    return {
      DEFAULT_USER_AVATAR,
      OFFENSE_OPTIONS,
      isSharing: false,
      editMessage: "",
      hasMoreReactions: false,
    };
  },
  computed: {
    ...mapState(useGeneralStore, ["userData"]),
    message() {
      return this.getMessage(this.messageId);
    },
    authorId() {
      return this.message.userId;
    },
    isAuthor() {
      return this.isMessageAuthor(this.messageId);
    },
    reactions() {
      return this.message.reaction ?? [];
    },
    reactionsWithUserData() {
      return this.reactions.map(reaction => {
        return {
          ...reaction,
          current_user_reacted: this.userData.user_id === reaction.user_id,
        };
      });
    },
    numReplies() {
      return this.message.numApprovedReplies;
    },
    canReply() {
      // TODO this will come from store somewhere
      return true;
    },
    getReplies() {
      switch (this.numReplies) {
        case 0:
          return "Reply";
        case 1:
          return "1 Reply";
        default:
          return `${this.numReplies} Replies`;
      }
    },
    activeMessageReplies() {
      return this.message.replies ?? [];
    },
    latestReply() {
      if (this.activeMessageReplies.length > 0) {
        const firstReply = this.activeMessageReplies[this.activeMessageReplies.length - 1];
        const lastReply = this.activeMessageReplies[0];

        if (firstReply.timestampCreated > lastReply.timestampCreated) {
          return firstReply;
        }
        return lastReply;
      }
      return {};
    },
    latestTimestampCreated() {
      if (this.latestReply.timestampCreated) {
        return timeAgo(this.latestReply.timestampCreated);
      }
      return "";
    },
    isActive() {
      return this.message.status === "active";
    },
  },
  methods: {
    ...mapActions(useMessageStore, [
      "getMessage",
      "isMessageAuthor",
      "handleReaction",
    ]),
    reply() {
      this.$emit("open:reply-modal");
    },
    reactionSelected(reaction) {
      this.handleReaction(reaction, this.messageId);
    },
  },
  styleGuide: () => ({
    borderRadius: { "size.borderRadius": "standard" },
  }),
};
</script>


<style lang="scss" scoped>
.reply-btn {
  cursor: pointer;
}

.reply-time {
  position: relative;
  top: 1px;
}
</style>
